<template>
    <div :class="getPageContainerClasses">
        <form id="customer-signup" method="POST" :action="getFormAction" @submit="onSubmit">
            <input type="hidden" name="_token" :value="csrfToken">
            <honeypot-fields />
            
            <div class="mb-6 space-y-4">
                <!-- Form errors -->
                <form-errors :store-form-errors="formErrors"/>

                <!-- Custom fields -->
                <referral-portal-signup-custom-fields />
            </div>

            <!-- Terms of Use -->
            <div v-if="portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_url || portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_text" class="space-x-2 flex text-sm my-6">
                <input
                    type="checkbox"
                    name="tos"
                    id="tos"
                    :class="getCheckboxInputStyle"
                    v-model="formData.tos"
                />
                <label for="tos" :class="getPageContentTextColorClass">
                    <editable-text-field
                        text-type="span"
                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_i_agree"
                    >
                    </editable-text-field>
                    
                    <!-- Terms modal or link -->
                    <template v-if="portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_text">
                        <modal 
                            :title="portalContentAndDesignStore.translationSettings.portal_translation_signup_terms_of_use || 'Terms of Use'"
                            size="huge" 
                            class="text-gray-900 inline-block"
                        >
                            <template #toggle>
                                <a href="#" class="underline text-opacity-100" :class="getPageLinkColorClass">
                                    <editable-text-field
                                        text-type="span"
                                        v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_terms_of_use"
                                    >
                                    </editable-text-field>
                                </a>
                            </template>
                            <template #body>
                                <div class="space-y-2 text-gray-500 affiliate-program-terms-modal">
                                    <editable-text-field
                                        text-type="div"
                                        v-model="portalContentAndDesignStore.signupPageSettings.signup.portal_signup_tos_text"
                                    >
                                    </editable-text-field>
                                </div>
                            </template>
                        </modal>
                    </template>
                    <template v-else-if="portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_url">
                        <a 
                            target="_blank" 
                            :href="portalContentAndDesignStore.signupPageSettings.signup.portal_signup_tos_url" 
                            class="underline text-opacity-100" 
                            :class="getPageLinkColorClass"
                        >
                            <editable-text-field
                                text-type="span"
                                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_terms_of_use"
                            >
                            </editable-text-field>
                        </a>
                    </template>
                </label>
            </div>

            <!-- Marketing consent -->
            <div v-if="portalContentAndDesignStore.signupPageSettings?.signup?.marketing_consent" class="space-x-2 flex text-sm my-6">
                <input
                    type="checkbox"
                    name="marketing_consent"
                    id="marketing_consent"
                    :class="getCheckboxInputStyle"
                    v-model="formData.marketing_consent"
                />
                <label for="marketing_consent" :class="getPageContentTextColorClass">
                    <editable-text-field
                        text-type="span"
                        v-model="portalContentAndDesignStore.signupPageSettings.signup.marketing_consent"
                    >
                    </editable-text-field>
                </label>
            </div>

            <!-- Turnstile Widget -->
            <div v-if="portalContentAndDesignStore.turnstileSettings?.turnstile_enabled" ref="turnstileContainer" class="my-4"></div>

            <!-- Submit button -->
            <editable-btn
                button-type="submit"
                :prevent-double-click="5000"
                size="huge"
                :class="getButtonClasses"
                :type="getButtonType"
                v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_register"
            >
                {{ portalContentAndDesignStore.translationSettings.portal_translation_signup_register || 'Register' }}
            </editable-btn>
        </form>
    </div>

    <!-- Login link outside container -->
    <div class="text-sm text-center" :class="getPageTextColorClass">
        <editable-text-field
            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_already_have_an_account"
            text-type="span"
        >
        </editable-text-field>
        {{ ' ' }}
        <editable-text-field
            v-model="portalContentAndDesignStore.translationSettings.portal_translation_signup_log_in"
            text-type="a"
            :href="portalContentAndDesignStore.isPreview ? '#' : portalContentAndDesignStore.getLoginRoute"
            class="hover:underline"
            :class="getPageLinkColorClass"
        >
        </editable-text-field>
    </div>
</template>

<script setup>
import {computed, onMounted, nextTick, ref} from "vue";
import {usePortalContentAndDesignStore} from "../../../../../stores/partners/portalContentAndDesignStore";
import FormErrors from "../../../Pub/Pages/Errors/FormErrors.vue";
import HoneypotFields from "../../../Pub/HoneypotFields.vue";
import ReferralPortalSignupCustomFields from "./Register/Partial/ReferralPortal/ReferralPortalSignupCustomFields.vue";

const portalContentAndDesignStore = usePortalContentAndDesignStore();

// Form data state
const formData = ref({
    tos: false,
    marketing_consent: false
});

// Form errors state
const formErrors = ref({
    validationErrors: null,
    responseError: null
});

const csrfToken = computed(() => {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
});

const getFormAction = computed(() => {
    // For referral portals, use the standard register route
    return '/register';
});



// Referral portal specific container classes
const getPageContainerClasses = computed(() => {
    const style = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_style || 'border';
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    const backgroundColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_background_color || 'white';
    const borderColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_border_color || 'gray-200';
    
    const classes = ['px-6 lg:px-12 py-8 lg:py-14'];
    
    // Background color
    if (backgroundColor.startsWith('#')) {
        classes.push('bg-content-custom-bg');
    } else {
        classes.push(`bg-${backgroundColor}`);
    }
    
    // Layout style
    if (style === 'border') {
        if (borderColor.startsWith('#')) {
            classes.push('border border-content-custom-color');
        } else {
            classes.push(`border border-${borderColor}`);
        }
    } else if (style === 'shadow') {
        classes.push('shadow-lg');
    }
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded-sm');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

// Referral portal specific text color class
const getPageContentTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-content-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

// Page-level text color (outside container)
const getPageTextColorClass = computed(() => {
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_text_color || 'gray-800';
    
    if (textColor.startsWith('#')) {
        return 'text-custom-text';
    } else {
        return `text-${textColor}`;
    }
});

// Page-level link color (outside container)
const getPageLinkColorClass = computed(() => {
    const linkColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_link_color || 'teal-600';
    
    if (linkColor.startsWith('#')) {
        return 'text-custom-link';
    } else {
        return `text-${linkColor}`;
    }
});

// Checkbox styling based on referral portal settings
const getCheckboxInputStyle = computed(() => {
    const inputStyle = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_input_style || 'white';
    const primaryColor = portalContentAndDesignStore.portalLayoutSettings?.portal_layout_brand_primary_color || 'teal-600';
    const textColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_text_color || 'gray-800';
    
    const classes = ['h-5 w-5 rounded cursor-pointer focus:ring-0 focus:ring-offset-0'];
    
    // Input style (background and border)
    if (inputStyle === 'white') {
        classes.push('bg-white text-gray-900 border-gray-300 focus:border-gray-400');
    } else {
        classes.push('bg-transparent');
        if (textColor.startsWith('#')) {
            classes.push('text-content-custom-text border-content-custom-text focus:border-content-custom-text');
        } else {
            classes.push(`text-${textColor} border-${textColor} focus:border-${textColor}`);
        }
    }
    
    // Primary color for checkbox
    if (primaryColor.startsWith('#')) {
        classes.push('text-custom-primary');
    } else {
        classes.push(`text-${primaryColor}`);
    }
    
    return classes.join(' ');
});

// Button styling based on referral portal settings
const getButtonClasses = computed(() => {
    const corners = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_layout_corners || 'rounded-xl';
    
    const classes = ['w-full'];
    
    // Corners
    if (corners === 'none') {
        classes.push('rounded');
    } else {
        classes.push(corners);
    }
    
    return classes.join(' ');
});

const getButtonType = computed(() => {
    const buttonColor = portalContentAndDesignStore.portalLayoutSettings?.referral_portal_content_button_color || 'teal';
    
    if (buttonColor.startsWith('#')) {
        return 'custom-content';
    } else {
        return buttonColor;
    }
});

const turnstileContainer = ref(null);
const turnstileToken = ref(null);
let turnstileWidgetId = null;

const initTurnstile = () => {
    const settings = portalContentAndDesignStore.turnstileSettings;
    if (!settings?.turnstile_enabled || !turnstileContainer.value) return;

    const renderWidget = () => {
        if (typeof window.turnstile === 'undefined') {
            setTimeout(renderWidget, 100);
            return;
        }
        if (turnstileWidgetId !== null) return;

        turnstileWidgetId = window.turnstile.render(turnstileContainer.value, {
            sitekey: settings.turnstile_site_key,
            callback: (token) => { turnstileToken.value = token; },
            'expired-callback': () => { turnstileToken.value = null; },
            'error-callback': () => { turnstileToken.value = null; },
        });
    };

    renderWidget();
};

onMounted(() => {
    nextTick(() => initTurnstile());
});

const onSubmit = (event) => {
    if (portalContentAndDesignStore.isPreview) {
        event.preventDefault();
        return;
    }

    event.preventDefault();

    const nameInput = document.getElementById('name');
    const emailInput = document.getElementById('email');

    if (!nameInput?.value) {
        alert('Please enter your name');
        return;
    }

    if (!emailInput?.value) {
        alert('Please enter your email address');
        return;
    }

    const hasTermsOfUse = portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_url || portalContentAndDesignStore.signupPageSettings?.signup?.portal_signup_tos_text;
    if (hasTermsOfUse && !formData.value.tos) {
        alert('Please accept the Terms of Use');
        return;
    }

    const form = document.getElementById('customer-signup');

    if (turnstileToken.value) {
        const existing = form.querySelector('input[name="cf-turnstile-response"]');
        if (!existing) {
            const tokenInput = document.createElement('input');
            tokenInput.type = 'hidden';
            tokenInput.name = 'cf-turnstile-response';
            tokenInput.value = turnstileToken.value;
            form.appendChild(tokenInput);
        } else {
            existing.value = turnstileToken.value;
        }
    }

    form.submit();
};
</script>
